import React, { useState, useEffect, useCallback, Fragment } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Chip, LinearProgress, TablePagination, Tooltip, Tabs, Tab, Collapse, Button,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { calibrationStatusChip } from '../../utils/calibrationBlockedReason';
import UploadMasterTab from '../../components/calibration/UploadMasterTab';
import CalibrationSettingsTab from '../../components/calibration/CalibrationSettingsTab';
import MaintenanceTab from '../../components/calibration/MaintenanceTab';
import { getAccessToken } from '../../services/authStorage';

const CalibrationLibrary = () => {
  const { user } = useAuth();

  // When opened in a floating window we get ?standalone=1 and an
  // ?observatory=<id> already chosen by the caller. In that mode we
  // hide the Refresh button (caller controls the window) and the
  // Observatory selector (the window is scoped to one observatory).
  const urlParams = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search) : null;
  const isStandalone = urlParams ? urlParams.has('standalone') : false;
  const presetObservatoryId = urlParams ? urlParams.get('observatory') : null;
  const lockedObservatory = isStandalone && presetObservatoryId;

  // State
  const [observatories, setObservatories] = useState([]);
  const [selectedObservatory, setSelectedObservatory] = useState(presetObservatoryId || '');
  const [summary, setSummary] = useState([]);
  const [frames, setFrames] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [sectionTab, setSectionTab] = useState(0); // 0=Frame Library, 1=Upload Master, 2=Settings
  const [tabValue, setTabValue] = useState(0);

  // Needs data
  const [needs, setNeeds] = useState(null);
  const [needsLoading, setNeedsLoading] = useState(false);
  // #1067: distinguish a load FAILURE from a genuinely-empty result. Without
  // this, a timeout (the needs query could take 8-30s server-side, past the
  // 10s client timeout) fell into the catch, set needs=null, and rendered the
  // same "No calibration needs data available" as a real empty state — so an
  // outage looked like "nothing needed". needsReloadKey lets Retry refetch.
  const [needsError, setNeedsError] = useState(null);
  const [needsReloadKey, setNeedsReloadKey] = useState(0);

  // Filters — 'all' is the explicit "no filter" sentinel so the
  // Select renders "All" instead of an empty value next to the floating
  // label.
  const [filterFrameType, setFilterFrameType] = useState('all');
  const [filterCamera, setFilterCamera] = useState('all');
  const [filterFilter, setFilterFilter] = useState('all');

  // Master accordion state
  const [expandedMasters, setExpandedMasters] = useState(new Set());
  const [masterComponents, setMasterComponents] = useState({});
  const [componentsLoading, setComponentsLoading] = useState(new Set());

  // Notification
  const [, setNotification] = useState({ open: false, message: '', severity: 'info' }); // TODO: render Snackbar using notification state

  // Load the active observatories the user has access to.
  // getObservatories is access-scoped server-side (non-admins get their member/
  // owner/org observatories) and excludes inactive/deactivated ones by default;
  // limit 100 loads the full active list. The is_inactive filter is defensive.
  useEffect(() => {
    const loadObservatories = async () => {
      try {
        if (!user) return;
        const response = await apiService.getObservatories({ limit: 100 });
        const obs = (response.data?.observatories || []).filter(o => !o.is_inactive);
        setObservatories(obs);
        if (obs.length > 0 && !selectedObservatory) {
          setSelectedObservatory(obs[0]._id);
        }
      } catch (error) {
        console.error('Failed to load observatories:', error);
      }
    };
    loadObservatories();
  }, [user]);

  // Load summary when observatory changes
  useEffect(() => {
    if (!selectedObservatory) return;
    const loadSummary = async () => {
      setSummaryLoading(true);
      try {
        const response = await apiService.getCalibrationSummary(selectedObservatory);
        setSummary(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to load calibration summary:', error);
        setSummary([]);
      } finally {
        setSummaryLoading(false);
      }
    };
    loadSummary();
  }, [selectedObservatory]);

  // Load frames
  const loadFrames = useCallback(async () => {
    if (!selectedObservatory) return;
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        is_master: tabValue === 0 ? 'true' : 'false',
      };
      if (tabValue === 1) params.exclude_mastered = 'true';
      if (filterFrameType && filterFrameType !== 'all') params.frame_type = filterFrameType;
      if (filterCamera && filterCamera !== 'all') params.camera_name = filterCamera;
      if (filterFilter && filterFilter !== 'all') params.filter = filterFilter;

      const response = await apiService.getCalibrationLibrary(selectedObservatory, params);
      setFrames(response.data || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Failed to load calibration frames:', error);
      setFrames([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [selectedObservatory, page, rowsPerPage, filterFrameType, filterCamera, filterFilter, tabValue]);

  useEffect(() => {
    if (tabValue <= 1) loadFrames();
  }, [loadFrames, tabValue]);

  // Load needs when Needs tab is selected
  useEffect(() => {
    if (tabValue !== 2 || !selectedObservatory) return;
    const loadNeeds = async () => {
      setNeedsLoading(true);
      setNeedsError(null);
      try {
        const response = await apiService.getCalibrationNeeds(selectedObservatory);
        setNeeds(response.data || null);
      } catch (error) {
        console.error('Failed to load calibration needs:', error);
        setNeeds(null);
        // A timeout / no-response is the #1067 symptom: surface it as a
        // retryable failure, not as "no data".
        const timedOut = error.code === 'ECONNABORTED' || !error.response;
        setNeedsError(
          timedOut
            ? 'Calibration needs took too long to load. This usually clears on a retry.'
            : 'Failed to load calibration needs.'
        );
      } finally {
        setNeedsLoading(false);
      }
    };
    loadNeeds();
  }, [tabValue, selectedObservatory, needsReloadKey]);

  // Get distinct cameras and filters from summary
  const cameras = [...new Set(summary.map(s => s._id?.camera_name).filter(Boolean))];
  const filters = [...new Set(frames.map(f => f.filter).filter(Boolean))];

  // Summary stats
  const totalMasters = summary.filter(s => s._id?.is_master).reduce((sum, s) => sum + s.count, 0);
  const totalIndividuals = summary.filter(s => !s._id?.is_master).reduce((sum, s) => sum + s.count, 0);

  // Download handler
  const handleDownload = (frame) => {
    const token = getAccessToken();
    const url = `/api/v1/calibration/frames/${frame._id}/download`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        const disposition = res.headers.get('Content-Disposition');
        const match = disposition && disposition.match(/filename="?([^"]+)"?/);
        const filename = match ? match[1]
          : `${frame.frame_type}_${frame.camera_name}_g${frame.gain}_${frame.binning}.fits`;
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        setNotification({ open: true, message: 'Download failed', severity: 'error' });
      });
  };

  // Toggle master accordion expansion and lazy-load components
  const toggleMasterExpand = async (frameId) => {
    const next = new Set(expandedMasters);
    if (next.has(frameId)) {
      next.delete(frameId);
      setExpandedMasters(next);
      return;
    }
    next.add(frameId);
    setExpandedMasters(next);

    if (!masterComponents[frameId]) {
      setComponentsLoading(prev => new Set(prev).add(frameId));
      try {
        const response = await apiService.getCalibrationFrameComponents(frameId);
        setMasterComponents(prev => ({ ...prev, [frameId]: response.data || [] }));
      } catch (error) {
        console.error('Failed to load component frames:', error);
        setMasterComponents(prev => ({ ...prev, [frameId]: [] }));
      } finally {
        setComponentsLoading(prev => {
          const s = new Set(prev);
          s.delete(frameId);
          return s;
        });
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
  };

  const frameTypeColor = (type) => {
    switch (type) {
      case 'dark': return 'default';
      case 'flat': return 'primary';
      case 'bias': return 'secondary';
      default: return 'default';
    }
  };

  // Frame table component (read-only - no delete action)
  const FrameTable = () => (
    loading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    ) : frames.length === 0 ? (
      <Alert severity="info">
        {tabValue === 0
          ? 'No master frames available for this observatory.'
          : 'No individual frames available for this observatory.'}
      </Alert>
    ) : (
      <>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell>Type</TableCell>
                <TableCell>Camera</TableCell>
                <TableCell>Gain</TableCell>
                <TableCell>Offset</TableCell>
                <TableCell>Binning</TableCell>
                <TableCell>Exposure</TableCell>
                <TableCell>Temp</TableCell>
                <TableCell>Filter</TableCell>
                <TableCell>Captured</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {frames.map((frame) => (
                <TableRow key={frame._id} hover>
                  <TableCell>
                    <Chip label={frame.frame_type} size="small" color={frameTypeColor(frame.frame_type)} />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`${frame.sensor_width}x${frame.sensor_height}`}>
                      <span>{frame.camera_name}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{frame.gain}</TableCell>
                  <TableCell>{frame.offset}</TableCell>
                  <TableCell>{frame.binning}</TableCell>
                  <TableCell>{frame.exposure_time_seconds}s</TableCell>
                  <TableCell>{frame.temperature_celsius != null ? `${frame.temperature_celsius.toFixed(1)}°C` : '-'}</TableCell>
                  <TableCell>{frame.filter || '-'}</TableCell>
                  <TableCell>{formatDate(frame.captured_at)}</TableCell>
                  <TableCell>{formatSize(frame.file_size_bytes)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => handleDownload(frame)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </>
    )
  );

  const selectedObservatoryName = observatories.find(o => o._id === selectedObservatory)?.name;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Calibration & Masters</Typography>
        {sectionTab === 0 && !lockedObservatory && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { loadFrames(); }}
            disabled={loading}
          >
            Refresh
          </Button>
        )}
      </Box>

      {/* Observatory selector — shared across all section tabs */}
      {!lockedObservatory && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Observatory</InputLabel>
              <Select
                value={selectedObservatory}
                label="Observatory"
                onChange={(e) => {
                  setSelectedObservatory(e.target.value);
                  setPage(0);
                  setFilterCamera('all');
                  setFilterFilter('all');
                }}
              >
                {observatories.map((obs) => (
                  <MenuItem key={obs._id} value={obs._id}>{obs.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}

      {/* Section tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={sectionTab} onChange={(_, v) => setSectionTab(v)}>
          <Tab label="Frame Library" />
          <Tab label="Upload Master" />
          <Tab label="Settings" />
        </Tabs>
      </Box>

      {sectionTab === 0 && (<>
      {/* Frame filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Frame Type</InputLabel>
            <Select
              value={filterFrameType}
              label="Frame Type"
              onChange={(e) => { setFilterFrameType(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="dark">Dark</MenuItem>
              <MenuItem value="flat">Flat</MenuItem>
              <MenuItem value="bias">Bias</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Camera</InputLabel>
            <Select
              value={filterCamera}
              label="Camera"
              onChange={(e) => { setFilterCamera(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">All Cameras</MenuItem>
              {cameras.map((cam) => (
                <MenuItem key={cam} value={cam}>{cam}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter</InputLabel>
            <Select
              value={filterFilter}
              label="Filter"
              onChange={(e) => { setFilterFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">All Filters</MenuItem>
              {filters.map((f) => (
                <MenuItem key={f} value={f}>{f}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Summary Cards */}
      {!summaryLoading && selectedObservatory && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="primary">{totalMasters}</Typography>
                <Typography variant="body2" color="text.secondary">Master Frames</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4">{totalIndividuals}</Typography>
                <Typography variant="body2" color="text.secondary">Individual Frames</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4">{cameras.length}</Typography>
                <Typography variant="body2" color="text.secondary">Cameras</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4">
                  {summary.filter(s => s._id?.is_master).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">Master Configurations</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, v) => { setTabValue(v); setPage(0); }}>
            <Tab label="Masters" />
            <Tab label="Individual Frames" />
            <Tab label="Needs" />
            <Tab label="Maintenance" />
          </Tabs>
        </Box>
        <CardContent>
          {/* Masters Tab - Table with expandable component frames */}
          {tabValue === 0 && (
            loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : frames.length === 0 ? (
              <Alert severity="info">
                No master frames available for this observatory.
              </Alert>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell>Type</TableCell>
                        <TableCell>Camera</TableCell>
                        <TableCell>Gain</TableCell>
                        <TableCell>Offset</TableCell>
                        <TableCell>Binning</TableCell>
                        <TableCell>Exposure</TableCell>
                        <TableCell>Temp</TableCell>
                        <TableCell>Filter</TableCell>
                        <TableCell>Captured</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Frames</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {frames.map((frame) => {
                        const componentCount = frame.master_metadata?.source_frame_ids?.length || 0;
                        const components = masterComponents[frame._id];
                        const isCompLoading = componentsLoading.has(frame._id);
                        return (
                          <Fragment key={frame._id}>
                            <TableRow hover>
                              <TableCell>
                                <Chip label={frame.frame_type} size="small" color={frameTypeColor(frame.frame_type)} />
                              </TableCell>
                              <TableCell>
                                <Tooltip title={`${frame.sensor_width}x${frame.sensor_height}`}>
                                  <span>{frame.camera_name}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell>{frame.gain}</TableCell>
                              <TableCell>{frame.offset}</TableCell>
                              <TableCell>{frame.binning}</TableCell>
                              <TableCell>{frame.exposure_time_seconds}s</TableCell>
                              <TableCell>{frame.temperature_celsius != null ? `${frame.temperature_celsius.toFixed(1)}°C` : '-'}</TableCell>
                              <TableCell>{frame.filter || '-'}</TableCell>
                              <TableCell>{formatDate(frame.captured_at)}</TableCell>
                              <TableCell>{formatSize(frame.file_size_bytes)}</TableCell>
                              <TableCell>
                                {componentCount > 0 ? (
                                  <Tooltip title={`View ${componentCount} component frames`}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => toggleMasterExpand(frame._id)}
                                    >
                                      <FolderOpenIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Download">
                                  <IconButton size="small" onClick={() => handleDownload(frame)}>
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                            {componentCount > 0 && (
                              <TableRow>
                                <TableCell colSpan={12} sx={{ padding: 0, border: 'none' }}>
                                  <Collapse in={expandedMasters.has(frame._id)} timeout="auto" unmountOnExit>
                                    <Box sx={{ backgroundColor: 'action.hover', pl: 6, pr: 2, py: 1 }}>
                                      {isCompLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                          <CircularProgress size={20} />
                                        </Box>
                                      ) : components && components.length > 0 ? (
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>#</TableCell>
                                              <TableCell>Captured</TableCell>
                                              <TableCell>Temp</TableCell>
                                              <TableCell>Exposure</TableCell>
                                              <TableCell>Size</TableCell>
                                              <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {components.map((comp, idx) => (
                                              <TableRow key={comp._id}>
                                                <TableCell>{idx + 1}</TableCell>
                                                <TableCell>{formatDate(comp.captured_at)}</TableCell>
                                                <TableCell>{comp.temperature_celsius != null ? `${comp.temperature_celsius.toFixed(1)}°C` : '-'}</TableCell>
                                                <TableCell>{comp.exposure_time_seconds}s</TableCell>
                                                <TableCell>{formatSize(comp.file_size_bytes)}</TableCell>
                                                <TableCell align="right">
                                                  <Tooltip title="Download">
                                                    <IconButton size="small" onClick={() => handleDownload(comp)}>
                                                      <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                          Component frames not found — they may have been deleted.
                                        </Typography>
                                      )}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            )
          )}

          {/* Individual Frames Tab */}
          {tabValue === 1 && <FrameTable />}

          {/* Needs Tab */}
          {tabValue === 2 && (
            needsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : needsError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => setNeedsReloadKey((k) => k + 1)}>
                    Retry
                  </Button>
                }
              >
                {needsError}
              </Alert>
            ) : !needs ? (
              <Alert severity="info">No calibration needs data available.</Alert>
            ) : (
              <Box>
                {/* Active Flat Capture */}
                {needs.active_flat_capture && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {needs.active_flat_capture.status === 'started' ? 'Starting...' : 'Capturing...'}{' '}
                      {needs.active_flat_capture.mode === 'sky'
                        ? `Sky Flats (${needs.active_flat_capture.twilight_direction || 'twilight'})`
                        : 'Panel Flats'}
                      {needs.active_flat_capture.current_filter && ` \u2014 Filter: ${needs.active_flat_capture.current_filter}`}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={needs.active_flat_capture.frames_requested > 0
                          ? (needs.active_flat_capture.frames_received / needs.active_flat_capture.frames_requested) * 100
                          : 0}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption">
                        {needs.active_flat_capture.frames_received}/{needs.active_flat_capture.frames_requested} frames
                      </Typography>
                    </Box>
                    {needs.active_flat_capture.filters_planned?.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Filters: {needs.active_flat_capture.filters_planned.map(f => f.filter).join(', ')}
                      </Typography>
                    )}
                  </Alert>
                )}

                {/* Expiry Warnings */}
                {needs.expiry_warnings?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon color="warning" fontSize="small" /> Expiry Warnings
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            <TableCell>Status</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Camera</TableCell>
                            <TableCell>Gain</TableCell>
                            <TableCell>Binning</TableCell>
                            <TableCell>Filter</TableCell>
                            <TableCell>Expires In</TableCell>
                            <TableCell>Dark Available</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {needs.expiry_warnings.map((w, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                <Chip
                                  label={w.status === 'expired' ? 'Expired' : 'Expiring'}
                                  size="small"
                                  color={w.status === 'expired' ? 'error' : 'warning'}
                                />
                              </TableCell>
                              <TableCell><Chip label={w.frame_type} size="small" color={frameTypeColor(w.frame_type)} /></TableCell>
                              <TableCell>{w.camera_name}</TableCell>
                              <TableCell>{w.gain}</TableCell>
                              <TableCell>{w.binning}</TableCell>
                              <TableCell>{w.filter || '-'}</TableCell>
                              <TableCell>{w.expires_in_days > 0 ? `${w.expires_in_days} days` : 'Expired'}</TableCell>
                              <TableCell>
                                {w.frame_type === 'flat' ? (
                                  w.prerequisite_dark_available
                                    ? <CheckCircleIcon color="success" fontSize="small" />
                                    : <WarningIcon color="error" fontSize="small" />
                                ) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Uncovered Configs */}
                {needs.uncovered_configs?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon color="info" fontSize="small" /> Uncovered Camera Configurations
                    </Typography>
                    <Alert severity="info" sx={{ mb: 1 }}>
                      These configurations have light frames but no matching calibration masters or frames in progress.
                    </Alert>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            <TableCell>Type</TableCell>
                            <TableCell>Camera</TableCell>
                            <TableCell>Sensor</TableCell>
                            <TableCell>Gain</TableCell>
                            <TableCell>Binning</TableCell>
                            <TableCell>Readout</TableCell>
                            <TableCell>Filter</TableCell>
                            <TableCell>Exposure</TableCell>
                            <TableCell>Avg Temp</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {needs.uncovered_configs.map((cfg, i) => (
                            <TableRow key={i}>
                              <TableCell><Chip label={cfg.frame_type} size="small" color={frameTypeColor(cfg.frame_type)} /></TableCell>
                              <TableCell>{cfg.camera_name}</TableCell>
                              <TableCell>{cfg.sensor_width}x{cfg.sensor_height}</TableCell>
                              <TableCell>{cfg.gain}</TableCell>
                              <TableCell>{cfg.binning}</TableCell>
                              <TableCell>{cfg.readout_mode || '-'}</TableCell>
                              <TableCell>{cfg.filter || '-'}</TableCell>
                              <TableCell>{cfg.exposure_time_seconds != null ? `${cfg.exposure_time_seconds}s` : '-'}</TableCell>
                              <TableCell>{cfg.avg_temperature != null ? `${cfg.avg_temperature}\u00B0C` : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Accumulation Progress */}
                <Typography variant="subtitle1" gutterBottom>
                  Accumulation Progress (target: {needs.min_frames_for_master} frames)
                </Typography>
                {needs.accumulation?.length === 0 && !needs.uncovered_configs?.length ? (
                  <Alert severity="success">
                    No calibration needs detected within the {needs.lookback_days}-day lookback window.
                  </Alert>
                ) : needs.accumulation?.length === 0 ? (
                  <Alert severity="info">
                    No individual frames found within the {needs.lookback_days}-day lookback window.
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                          <TableCell>Type</TableCell>
                          <TableCell>Camera</TableCell>
                          <TableCell>Gain</TableCell>
                          <TableCell>Binning</TableCell>
                          <TableCell>Exposure</TableCell>
                          <TableCell>Readout</TableCell>
                          <TableCell>Filter</TableCell>
                          <TableCell>Avg Temp</TableCell>
                          <TableCell>Progress</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {needs.accumulation.map((group, i) => (
                          <TableRow key={i}>
                            <TableCell><Chip label={group.frame_type} size="small" color={frameTypeColor(group.frame_type)} /></TableCell>
                            <TableCell>{group.camera_name}</TableCell>
                            <TableCell>{group.gain}</TableCell>
                            <TableCell>{group.binning}</TableCell>
                            <TableCell>{group.exposure_time_seconds}s</TableCell>
                            <TableCell>{group.readout_mode || '-'}</TableCell>
                            <TableCell>{group.filter || '-'}</TableCell>
                            <TableCell>{group.avg_temperature != null ? `${group.avg_temperature}°C` : '-'}</TableCell>
                            <TableCell sx={{ minWidth: 120 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={group.progress * 100}
                                  sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                                  color={group.ready_for_master ? 'success' : 'primary'}
                                />
                                <Tooltip title={group.count_note || ''} placement="top">
                                  <Typography variant="caption">
                                    {group.usable_count != null ? group.usable_count : group.count}/{group.target}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                if (group.ready_for_master) {
                                  return <Chip label="Ready" size="small" color="success" />;
                                }
                                // Not ready: surface the predicate's blocked_reason. #700
                                const chip = calibrationStatusChip(group);
                                if (!chip) return null;
                                const chipEl = (
                                  <Chip
                                    label={chip.label}
                                    size="small"
                                    color={chip.color}
                                    variant={chip.variant}
                                    icon={chip.color === 'warning' ? <WarningIcon /> : undefined}
                                  />
                                );
                                return chip.tooltip
                                  ? <Tooltip title={chip.tooltip}>{chipEl}</Tooltip>
                                  : chipEl;
                              })()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )
          )}

          {/* Maintenance Tab */}
          {tabValue === 3 && (
            <MaintenanceTab
              observatoryId={selectedObservatory}
              frameType={filterFrameType}
              onChanged={async () => {
                try {
                  const summaryResponse = await apiService.getCalibrationSummary(selectedObservatory);
                  setSummary(Array.isArray(summaryResponse.data) ? summaryResponse.data : []);
                } catch (err) {
                  console.error('Failed to refresh summary after maintenance op:', err);
                }
                loadFrames();
              }}
            />
          )}
        </CardContent>
      </Card>
      </>)}

      {sectionTab === 1 && (
        <UploadMasterTab
          observatoryId={selectedObservatory}
          observatoryName={selectedObservatoryName}
          onUploaded={async () => {
            loadFrames();
            try {
              const summaryResponse = await apiService.getCalibrationSummary(selectedObservatory);
              setSummary(Array.isArray(summaryResponse.data) ? summaryResponse.data : []);
            } catch (err) {
              console.error('Failed to refresh summary after upload:', err);
            }
          }}
          onCancel={() => setSectionTab(0)}
        />
      )}

      {sectionTab === 2 && (
        <CalibrationSettingsTab
          observatoryId={selectedObservatory}
          observatoryName={selectedObservatoryName}
        />
      )}
    </Box>
  );
};

export default CalibrationLibrary;
